import {
  ProtocolQuestionFormData,
  protocolQuestionFormDataSchema,
} from "@/features/protocol_set/features/protocol_questions/features/creation/types";
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import debounce from "lodash/debounce";
import { z } from "zod";

const questionEntrySchema = z.object({
  id: z.string(),
  startedOn: z.number(),
  // Toggles the visibility of the active question
  // This allows the user to view live changes instead of their local ones
  // without loosing existing work
  hidden: z.boolean(),
  data: protocolQuestionFormDataSchema,
});
const protocolQuestionEditStateSchema = z.object({
  questions: z.record(z.string(), questionEntrySchema),
  activeQuestionId: z.string().nullable(),
});
export type QuestionEntry = z.infer<typeof questionEntrySchema>;

export type ProtocolQuestionEditState = z.infer<typeof protocolQuestionEditStateSchema>;

export interface ProtocolQuestionEditRootState {
  protocolQuestionEdit: ProtocolQuestionEditState;
}

const STORAGE_KEY = "protocol-question-edit";

const loadInitialState = (): ProtocolQuestionEditState => {
  const stored = localStorage.getItem(STORAGE_KEY);
  try {
    if (stored) {
      const parsed = JSON.parse(stored) as ProtocolQuestionEditState;
      return protocolQuestionEditStateSchema.parse(parsed);
    }
  } catch (e) {
    console.error("Failed to load edit state from localStorage", e, "received", stored);
  }
  return { questions: {}, activeQuestionId: null };
};

export function saveState(state: ProtocolQuestionEditState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error("Failed to save edit state to localStorage", e);
  }
}

export const saveStateDebounced = debounce(saveState, 1000);
const initialState: ProtocolQuestionEditState = loadInitialState();

const protocolQuestionEditSlice = createSlice({
  name: "protocolQuestionEdit",
  initialState,
  reducers: {
    setQuestionId(state, action: PayloadAction<string>) {
      state.activeQuestionId = action.payload;
    },
    clearQuestionId(state) {
      state.activeQuestionId = null;
    },
    startQuestionEdit(state, action: PayloadAction<ProtocolQuestionFormData>) {
      const id = state.activeQuestionId!;
      state.questions[id] = {
        data: action.payload,
        hidden: false,
        startedOn: Date.now(),
        id,
      };
    },
    updateQuestion(state, action: PayloadAction<ProtocolQuestionFormData>) {
      const id = state.activeQuestionId!;
      state.questions[id] = {
        ...state.questions[id],
        data: action.payload,
      };
    },
    // Controls the visibility of the active question
    // See questionEntrySchema.hidden for more details
    toggleQuestionVisibility(state) {
      const id = state.activeQuestionId!;
      state.questions[id].hidden = !state.questions[id].hidden;
    },
    removeQuestion(state) {
      delete state.questions[state.activeQuestionId!];
    },
  },
});

export const protocolQuestionEditReducer = protocolQuestionEditSlice.reducer;
export const {
  setQuestionId,
  clearQuestionId,
  startQuestionEdit,
  updateQuestion,
  toggleQuestionVisibility,
  removeQuestion,
} = protocolQuestionEditSlice.actions;
